import { inject } from '@angular/core';
import {
  CanActivateFn,
  CanMatchFn,
  Router,
} from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FeatureFlagKey } from './feature-flag-keys';
import { FeatureFlagsService, type LDFlagValue } from './feature-flags.service';

export interface FeatureFlagGuardOptions {
  /** Where to redirect when the flag is false or null. Defaults to '/' */
  redirectTo?: string;
  /**
   * Value used when LaunchDarkly is unavailable or the flag is unset.
   * Defaults to `false` (deny). Use `true` for opt-out flags that should be on until LD loads.
   */
  defaultValue?: LDFlagValue;
}

function isFlagAllowed(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

/**
 * Creates a route guard that blocks access when the feature flag is false or null.
 * Always waits for the LD client to be ready before deciding.
 *
 * @example
 * canMatch: [featureFlagGuard(FeatureFlagKey.ENABLEFAAPP)],
 * canMatch: [featureFlagGuard(FeatureFlagKey.ENABLEFAAPP, { redirectTo: '/dashboard' })],
 * canMatch: [featureFlagGuard(FeatureFlagKey.ENABLEASKNAVI, { defaultValue: true })],
 */
export function featureFlagGuard(
  flagKey: FeatureFlagKey,
  options: FeatureFlagGuardOptions = {}
): CanActivateFn & CanMatchFn {
  const { redirectTo = '/', defaultValue = false } = options;

  return (): Promise<boolean> => {
    const flags = inject(FeatureFlagsService);
    const router = inject(Router);
    return firstValueFrom(flags.getValue$(flagKey, defaultValue)).then((value) => {
      if (!isFlagAllowed(value)) {
        router.navigateByUrl(redirectTo);
        return false;
      }
      return true;
    });
  };
}
